import React, { useRef, useState, useEffect, useCallback } from 'react';
import '../component_styles/cardeditor.css'; // Custom CSS for positioning elements
import Modal from 'react-modal'; // Use a modal package, e.g., react-modal

// Styling for the modal — themed to match the rest of the site and centered
// on screen (the overlay is a flex container, the content is centered in it).
const customModalStyles = {
  overlay: {
    backgroundColor: 'rgba(20, 12, 6, 0.6)', // Dimmed, warm-tinted background
    backdropFilter: 'blur(4px)',
    zIndex: 1300, // Ensure modal is above other elements
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  content: {
    position: 'relative',
    inset: 'auto',
    margin: '0',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '24px',
    borderRadius: 'var(--radius-lg, 16px)',
    border: '1px solid var(--color-border, rgba(0,0,0,0.1))',
    boxShadow: 'var(--shadow-high, 0 24px 60px rgba(0,0,0,0.4))',
    backgroundColor: 'var(--color-surface, #241a11)',
    color: 'var(--color-text-primary, #f6ecdd)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center', // Center content
  },
};

// The card is authored on the same 1440px-wide coordinate system as the
// Python generator. Every size is expressed as a fraction of the card's
// live pixel width (--cw), so the card looks identical on every device and
// exports 1:1 regardless of the on-screen display size.

// Modal component for selecting or uploading an image
const CardEditor = ({ template, backgroundImage, foregroundImage, handleForegroundUpload, icons, handleBackgroundUpload, insertImageAtCursor }) => {
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [selectedURL, setSelectedURL] = useState(''); // Dropdown selection
  const foregroundRef = useRef(null); // Ref for hidden file input
  const [dragging, setDragging] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null); // Ref to track the image's position
  const [isDragging, setIsDragging] = useState(false); // New state to track dragging
  const [scale, setScale] = useState(1); // Track zoom scale
  const [fgdragging, fgsetDragging] = useState(false);
  const [fgimagePosition, fgsetImagePosition] = useState({ x: 0, y: 0 });
  const fgimageRef = useRef(null); // Ref to track the image's position
  const [fgisDragging, fgsetIsDragging] = useState(false); // New state to track dragging
  const [fgscale, fgsetScale] = useState(1); // Track zoom scale
  const templateRef = useRef(null); // Ref to track the image's position
  const fgRef = useRef(null); // Ref to track the image's position
  const editorRef = useRef(null); // Ref to the card shell (sizing context)
  const [content, setContent] = useState('Ability description'); // Holds the current content with text and images
  const [spans, setSpans] = useState([{ id: 1, content: content }]); // Array of spans with unique IDs
  const [nextId, setNextId] = useState(2);
  const [energy, setEnergy] = useState('?');
  const [power, setPower] = useState('?');
  const [name, setName] = useState('CARD NAME');
  const defaultImageURL = 'https://t4.ftcdn.net/jpg/04/81/13/43/360_F_481134373_0W4kg2yKeBRHNEklk4F9UXtGHdub3tYk.jpg';
  const bgRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering options
  const [filteredIcons, setFilteredIcons] = useState(icons);

  // Current pixel width of the card (== Python's 1440 coordinate space).
  const getCardWidth = useCallback(() => {
    const el = editorRef.current || document.getElementById('editor');
    return (el && el.offsetWidth) || 400;
  }, []);

  const handleInputChange = (e) => {
    setContent(e.target.innerHTML.split('').reverse().join('')); // Update content state based on input
  };

  const handlePowerChange = (e) => {
    setPower(e.target.innerHTML.split('').reverse().join('')); // Update content state based on input
  };

  const handleEnergyChange = (e) => {
    setEnergy(e.target.innerHTML.split('').reverse().join('')); // Update content state based on input
  };

  const handleNameChange = (e) => {
    setName(e.target.innerHTML.split('').reverse().join('')); // Update content state based on input
  };

  useEffect(() => {
    const imageElement = templateRef.current;
    const fgImageElement = fgRef.current;

    // Add event listener for background zoom
    if (imageElement) {
      imageElement.addEventListener('wheel', handleWheelZoom, { passive: false });
      imageElement.addEventListener('touchmove', handlePinchZoom, { passive: false })
      imageElement.addEventListener('touchend', handleTouchEnd, { passive: false })
    }

    // Add event listener for foreground zoom
    if (fgImageElement) {
      fgImageElement.addEventListener('wheel', fghandleWheelZoom, { passive: false });
    }

    // Cleanup the event listeners when component unmounts
    return () => {
      if (imageElement) {
        imageElement.removeEventListener('wheel', handleWheelZoom, { passive: false });
        imageElement.removeEventListener('touchmove', handlePinchZoom, { passive: false })
        imageElement.removeEventListener('touchend', handleTouchEnd, { passive: false })
      }
      if (fgImageElement) {
        fgImageElement.removeEventListener('wheel', fghandleWheelZoom, { passive: false });
      }
    };
  }, []);

  // Size embedded ability icons relative to the surrounding text (in em) so
  // they always match the text height — the top/bottom of an inline icon line
  // up with the top/bottom of the inline text — regardless of the adaptive
  // font size. Trigger icons (draw/play/return/turnstart) are a touch larger,
  // like the generator's badges.
  const syncAbilityIconSizes = useCallback((element) => {
    element.querySelectorAll('img').forEach((img) => {
      const isTrigger = /play|draw|return|start|turnstart/.test(img.src);
      img.style.width = 'auto';
      if (isTrigger) {
        // ~one line of text tall, so it lines up with the first row.
        img.style.height = '1.1em';
        img.style.display = 'block';
        img.style.marginTop = '0';
      } else {
        // A touch shorter than the text, anchored so the top stays put and the
        // bottom comes up slightly. Width auto keeps the icon's aspect ratio.
        img.style.height = '0.9em';
        img.style.verticalAlign = '-0.12em';
        img.style.marginTop = '0';
      }
    });
  }, []);

  // Size the ability text by how many lines it takes: larger when it fits in
  // fewer lines, smaller as it wraps to more — mirroring the real CUE cards.
  // Everything is measured against the card, then clamped so it never
  // overflows the ability box.
  const setFontSize = useCallback((element) => {
    if (!element) return;
    const cw = getCardWidth();
    const holder = document.getElementById('ability-desc-holder');
    const boxH = holder ? holder.clientHeight : cw * 0.17;

    // Font size (fraction of card width) per line count — 1 line is biggest.
    const gradient = [0.044, 0.040, 0.036, 0.032, 0.029]; // 1..5 lines
    const sizeForLines = (n) => cw * (gradient[Math.min(n, gradient.length) - 1] || 0.026);
    const minPx = cw * 0.018;

    const measureLines = (px) => {
      const lh = parseFloat(getComputedStyle(element).lineHeight);
      const lineH = Number.isFinite(lh) && lh > 0 ? lh : px * 1.15;
      return Math.max(1, Math.round(element.scrollHeight / lineH));
    };

    // The wrapped line count depends on the font size, which depends on the
    // line count — iterate a few times to converge.
    let px = sizeForLines(1);
    element.style.fontSize = `${px}px`;
    syncAbilityIconSizes(element);
    for (let i = 0; i < 4; i += 1) {
      const lines = measureLines(px);
      const next = sizeForLines(lines);
      if (Math.abs(next - px) < 0.5) break;
      px = next;
      element.style.fontSize = `${px}px`;
      syncAbilityIconSizes(element);
    }

    // Safety net: never overflow the ability box (height or width).
    let guard = 0;
    const overflowing = () =>
      element.scrollHeight > boxH + 1 || element.scrollWidth > element.clientWidth + 1;
    while (overflowing() && px > minPx && guard < 80) {
      px = Math.max(minPx, px - Math.max(0.5, px * 0.04));
      element.style.fontSize = `${px}px`;
      syncAbilityIconSizes(element);
      guard += 1;
    }
  }, [getCardWidth, syncAbilityIconSizes]);

  const setPEFontSize = useCallback((elementID) => {
    const element = document.getElementById(elementID);
    if (!element) return;
    const cw = getCardWidth();
    // Count actual visible characters (digits/sign), ignore whitespace/markup.
    const len = (element.innerText || '').replace(/\s+/g, '').length || 1;
    // Bigger for fewer digits, progressively smaller for more, so the number
    // always sits nicely inside the badge art. The step-down is intentionally
    // strong (a 2-digit number lands between the old 2- and 3-digit sizes).
    let k;
    if (len <= 1) k = 0.130;
    else if (len === 2) k = 0.0875;
    else if (len === 3) k = 0.068;
    else if (len === 4) k = 0.052;
    else k = 0.043;
    element.style.fontSize = `${cw * k}px`;
  }, [getCardWidth]);

  const setNameFontSize = useCallback((elementID) => {
    const element = document.getElementById(elementID);
    if (!element) return;
    const cw = getCardWidth();
    const wrapper = document.getElementById('card-name-wrapper');
    const maxPx = cw * 0.076; // matches the generator's 110px title cap
    const minPx = cw * 0.032;
    let px = maxPx;
    element.style.fontSize = `${px}px`;
    let guard = 0;
    while (
      wrapper &&
      (element.scrollWidth > element.clientWidth + 1 || element.scrollHeight > wrapper.clientHeight + 1) &&
      px > minPx &&
      guard < 60
    ) {
      px = Math.max(minPx, px - Math.max(0.5, px * 0.05));
      element.style.fontSize = `${px}px`;
      guard += 1;
    }
  }, [getCardWidth]);

  // Keep --cw in sync with the real pixel width of the card and re-fit text
  // whenever the card is resized (window resize, orientation change, etc.).
  const refitAll = useCallback(() => {
    const el = editorRef.current;
    if (el) el.style.setProperty('--cw', `${el.offsetWidth}px`);
    setPEFontSize('energy-cost');
    setPEFontSize('power');
    setNameFontSize('card-name');
    const table = document.getElementById('abilitiesTable');
    if (table) setFontSize(table);
  }, [setPEFontSize, setNameFontSize, setFontSize]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    // Prime --cw immediately so the first paint is correctly sized.
    el.style.setProperty('--cw', `${el.offsetWidth}px`);
    refitAll();

    const ro = new ResizeObserver(() => refitAll());
    ro.observe(el);
    window.addEventListener('resize', refitAll);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', refitAll);
    };
  }, [refitAll]);

  useEffect(() => {
    const desc = document.getElementById('abilitiesTable')
    if (desc) {
      let debounceTimer = null;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setFontSize(desc);
      }, 200); // Adjust delay as needed

      // Clean up timer
      return () => clearTimeout(debounceTimer);
    }
  }, [content, setFontSize])

  // The ability rows/text/icons are edited directly in the DOM (via
  // CustomCardPage), which does not flow through React state. Watch the
  // ability area so the text is re-fitted whenever it changes (add ability,
  // typing, inserting an icon).
  useEffect(() => {
    const desc = document.getElementById('ability-description');
    if (!desc) return;
    let t = null;

    // contentEditable inserts non-breaking spaces for leading/repeated spaces,
    // which never collapse — so leading/extra whitespace stays visible. We
    // convert those back to regular spaces (kept in the text, so nothing is
    // lost) so `white-space: normal` can hide leading/duplicate whitespace,
    // yet reveal it again if reflow moves it mid-line. The single nbsp we add
    // right before an icon (to keep a number bound to it) is left intact.
    const normalizeSpaces = (root) => {
      const sel = window.getSelection();
      const savedNode = sel && sel.rangeCount ? sel.anchorNode : null;
      const savedOffset = sel && sel.rangeCount ? sel.anchorOffset : 0;
      let changed = false;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((tn) => {
        if (tn.data.indexOf('\u00A0') === -1) return;
        const nextIsImg = tn.nextSibling && tn.nextSibling.nodeName === 'IMG';
        let s = tn.data;
        let trailing = '';
        if (nextIsImg && s.endsWith('\u00A0')) {
          trailing = '\u00A0';
          s = s.slice(0, -1);
        }
        const converted = s.replace(/\u00A0/g, ' ') + trailing;
        if (converted !== tn.data) {
          tn.data = converted; // same length, so the caret offset stays valid
          changed = true;
        }
      });
      if (changed && savedNode && root.contains(savedNode)) {
        try {
          const max = savedNode.nodeType === 3 ? savedNode.length : savedNode.childNodes.length;
          const r = document.createRange();
          r.setStart(savedNode, Math.min(savedOffset, max));
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
        } catch (e) { /* ignore caret restore failures */ }
      }
    };

    // While typing we ONLY re-fit the text size — never touch the text itself,
    // otherwise a just-typed (trailing) space gets normalized/collapsed and
    // appears to "undo" itself between keystrokes.
    const refit = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const table = document.getElementById('abilitiesTable');
        if (table) setFontSize(table);
      }, 120);
    };

    // Normalize whitespace only when a field loses focus (the user is done
    // editing it), so typing is never disrupted.
    const onFocusOut = () => {
      mo.disconnect(); // our own text mutation shouldn't retrigger the observer
      normalizeSpaces(desc);
      mo.observe(desc, { childList: true, subtree: true, characterData: true });
      const table = document.getElementById('abilitiesTable');
      if (table) setFontSize(table);
    };

    const mo = new MutationObserver(refit);
    mo.observe(desc, { childList: true, subtree: true, characterData: true });
    desc.addEventListener('focusout', onFocusOut);
    return () => {
      mo.disconnect();
      clearTimeout(t);
      desc.removeEventListener('focusout', onFocusOut);
    };
  }, [setFontSize]);

  useEffect(() => {
    if (document.getElementById('energy-cost')) setPEFontSize('energy-cost');
  }, [energy, setPEFontSize])

  useEffect(() => {
    if (document.getElementById('power')) setPEFontSize('power');
  }, [power, setPEFontSize])

  useEffect(() => {
    if (document.getElementById('card-name')) setNameFontSize('card-name');
  }, [name, setNameFontSize])

  const handleWheelZoom = (e) => {
    if (bgRef.current && bgRef.current.src && bgRef.current.src !== defaultImageURL) {
      e.preventDefault();
      const scaleChange = e.deltaY > 0 ? 0.97 : 1.03; // Zoom out on scroll down, zoom in on scroll up
      setScale((prevScale) => Math.max(0.5, Math.min(prevScale * scaleChange, 3))); // Limit zoom scale between 0.5 and 3
    }
  };

  const handlePinchZoom = (e) => {
    if (e.touches.length === 2 && bgRef.current && bgRef.current.src && bgRef.current.src !== defaultImageURL) {
      e.preventDefault(); // Prevent the page zoom
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scaleChange = dist / (imageRef.current?.prevDist || dist);
      setScale((prevScale) => Math.max(0.5, Math.min(prevScale * scaleChange, 3)));
      imageRef.current.prevDist = dist; // Store the current distance
    }
  };

  const handleTouchEnd = () => {
    if (bgRef.current && bgRef.current.src && bgRef.current.src !== defaultImageURL) {
      imageRef.current.prevDist = null; // Reset previous distance on touch end
    }
  };

  const handleMouseDown = (e) => {
    if (bgRef.current && bgRef.current.src && bgRef.current.src !== defaultImageURL) {
      setDragging(true);
      setIsDragging(false); // Reset dragging state on mouse down
      imageRef.current = { startX: e.clientX, startY: e.clientY }; // Track the starting mouse position
    }
  };

  const handleMouseMove = (e) => {
    if (fgdragging) return; // Prevent background dragging if foreground is dragging
    if (dragging && bgRef.current && bgRef.current.src && bgRef.current.src !== defaultImageURL) {
      const dx = e.clientX - imageRef.current.startX;
      const dy = e.clientY - imageRef.current.startY;
      setImagePosition(prevPos => ({
        x: prevPos.x + dx,
        y: prevPos.y + dy,
      }));
      imageRef.current.startX = e.clientX;
      imageRef.current.startY = e.clientY;
      setIsDragging(true); // Set dragging to true during mouse move
    }
  };

  const handleMouseUp = () => {
    if (fgdragging || !(dragging && bgRef.current && bgRef.current.src && bgRef.current.src !== defaultImageURL)) return; // Prevent background stop if foreground is dragging
    setDragging(false);
  };

  const fghandleWheelZoom = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const scaleChange = e.deltaY > 0 ? 0.97 : 1.03; // Zoom out on scroll down, zoom in on scroll up
    fgsetScale((prevScale) => Math.max(0.5, Math.min(prevScale * scaleChange, 3))); // Limit zoom scale between 0.5 and 3
    // Dummy line to be removed when custom album icons are supported
    fgsetScale(() => 1)
  };

  /* eslint-disable no-unused-vars */
  const fghandleTouchEnd = () => {
    fgimageRef.current.prevDist = null; // Reset previous distance on touch end
  };

  const fghandleMouseDown = (e) => {
    e.stopPropagation();  // Prevent event from reaching parent
    fgsetDragging(true);
    fgsetIsDragging(false); // Reset dragging state on mouse down
    fgimageRef.current = { startX: e.clientX, startY: e.clientY }; // Track the starting mouse position
  };

  const fghandleMouseMove = (e) => {
    e.stopPropagation();  // Prevent event from reaching parent
    if (fgdragging) {
      const dx = e.clientX - fgimageRef.current.startX;
      const dy = e.clientY - fgimageRef.current.startY;
      fgsetImagePosition(prevPos => ({
        x: prevPos.x + dx,
        y: prevPos.y + dy,
      }));
      fgimageRef.current.startX = e.clientX;
      fgimageRef.current.startY = e.clientY;
      fgsetIsDragging(true); // Set dragging to true during mouse move
    }
  };

  const fghandleMouseUp = () => {
    fgsetDragging(false);
  };

  const handleForegroundClick = () => {
    setIsModalOpen(true); // Open the modal on foreground click
  };

  const handleDropdownChange = (e) => {
    setSelectedURL(e.target.value); // Set the selected URL from the dropdown
  };

  const handleModalConfirm = () => {
    const regexp = /^[a-z]+$/i;
    if (selectedURL) {
      if (regexp.test(selectedURL)) {
        handleForegroundUpload({ target: { files: [{ url: selectedURL }] } }); // Simulate file upload with URL
      }
      else {
        handleForegroundUpload({ target: { files: [{ url: icons[selectedURL] }] } })
      }
    }
    setIsModalOpen(false); // Close the modal
  };

  const handleURLModalConfirm = (url) => {
    if (url) {
        handleForegroundUpload({ target: { files: [{ url: url }] } })
    }
    setIsModalOpen(false); // Close the modal
  };

  const handleFileChange = (e) => {
    if (fgisDragging) {
      e.preventDefault(); // Prevent the click if dragging
    }
    handleForegroundUpload(e); // Upload the file
    setIsModalOpen(false); // Close the modal once a file is uploaded
  };

  const handleBackgroundUploadClick = (e) => {
    if (isDragging) {
      e.preventDefault(); // Prevent the click if dragging
    }
  };

  const handleTouchStart = (e) => {
    setDragging(true);
    setIsDragging(false);
    const touch = e.touches[0];
    imageRef.current = { startX: touch.clientX, startY: touch.clientY };
  };

  const handleTouchMove = (e) => {
    if (fgdragging) return; // Prevent background dragging if foreground is dragging
    if (dragging && bgRef.current && bgRef.current.src && bgRef.current.src !== defaultImageURL) {
      const touch = e.touches[0];
      const dx = touch.clientX - imageRef.current.startX;
      const dy = touch.clientY - imageRef.current.startY;
      setImagePosition(prevPos => ({
        x: prevPos.x + dx,
        y: prevPos.y + dy,
      }));
      imageRef.current.startX = touch.clientX;
      imageRef.current.startY = touch.clientY;
      setIsDragging(true); // Set dragging to true during touch move
    }
  };

  const fghandleTouchStart = (e) => {
    e.stopPropagation();
    fgsetDragging(true);
    fgsetIsDragging(false);
    const touch = e.touches[0];
    fgimageRef.current = { startX: touch.clientX, startY: touch.clientY };
  };

  const fghandleTouchMove = (e) => {
    if (fgdragging) {
      const touch = e.touches[0];
      const dx = touch.clientX - fgimageRef.current.startX;
      const dy = touch.clientY - fgimageRef.current.startY;
      fgsetImagePosition(prevPos => ({
        x: prevPos.x + dx,
        y: prevPos.y + dy,
      }));
      fgimageRef.current.startX = touch.clientX;
      fgimageRef.current.startY = touch.clientY;
      fgsetIsDragging(true); // Set dragging to true during touch move
    }
  };

  const handleKeyDown = (e, spanIndex) => {
    const updatedSpans = [...spans];

    // Handle Enter key to add a new span
    if (e.key === 'Enter') {
      e.preventDefault();
      const newSpan = { id: nextId, content: 'Ability Description' };
      updatedSpans.splice(spanIndex + 1, 0, newSpan); // Insert new span after the current one
      setSpans(updatedSpans);
      setNextId(nextId + 1);
    }

    // Handle Delete key to remove an empty span
    if (e.key === 'Delete' && updatedSpans[spanIndex].content === '') {
      e.preventDefault();
      if (updatedSpans.length > 1) {
        updatedSpans.splice(spanIndex, 1); // Remove the empty span
        setSpans(updatedSpans);
      }
    }
  };

  // Handle span content change
  const handleSpanChange = (e, spanIndex) => {
    const updatedSpans = [...spans];
    updatedSpans[spanIndex].content = e.target.innerText;
    setSpans(updatedSpans);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value); // Update search term
  };

  const handleOptionClick = (url, name) => {
    setSelectedURL(url); // Set the selected URL when an option is clicked
    setSearchTerm(name); // Optionally set search term to the selected icon's name
    handleURLModalConfirm(url);
  };

  useEffect(() => {
    setFilteredIcons(icons.filter(icon => icon.name.toLowerCase().includes(searchTerm.toLowerCase())))
  }, [searchTerm, icons])

  return (
    <div id="editor" className="editor" ref={editorRef}>
      <div id='toCapture'>
        <div
          id="template-holder"
          style={{
            width: '100%',
            height: '100%',
            lineHeight: '0',
            cursor: dragging ? 'grabbing' : 'grab', // Change cursor during drag
            borderRadius: '6%', // Set your desired border radius here
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <img
            id="template"
            src={backgroundImage}
            alt=""
            style={{
              position: 'absolute',
              top: `${imagePosition.y}px`,
              left: `${imagePosition.x}px`,
              width: `${scale * 100}%`, // Scale the background image
              height: 'auto', // Maintain aspect ratio
              objectFit: 'contain', // Cover the whole area
              objectPosition: 'center',
              zIndex: -1, // Ensure it stays behind other content
            }}
            crossOrigin="anonymous"
            ref={bgRef}
          />
          <input
            type="file"
            accept="image/*"
            style={{
              opacity: 0,              // Make the input invisible
              position: 'absolute',     // Position it absolutely in the container
              left: '25%',
              top: 0,
              width: '75%',            // Make it cover the entire div
              height: '60%',
              cursor: 'pointer',        // Show the pointer when hovering over it
            }}
            onClick={handleBackgroundUploadClick} // Attach the click handler
            onChange={handleBackgroundUpload}
            onMouseDown={handleMouseDown} // Start dragging
            onMouseMove={handleMouseMove} // Move the image
            onMouseUp={handleMouseUp} // Stop dragging
            onMouseLeave={handleMouseUp} // Stop dragging if the mouse leaves the container
            onTouchEnd={handleTouchEnd}   // Reset pinch zoom on touch end
            onTouchStart={handleTouchStart}  // For mobile dragging
            onTouchMove={handleTouchMove}    // For mobile dragging
            ref={templateRef}
          />
          <img id="template" src={template.url} alt="" className="template-img" crossOrigin="anonymous" />
          <div
            onClick={handleForegroundClick}
            style={{
              minWidth: '10%',
              position: 'absolute',
              top: `calc(61.4% + ${fgimagePosition.y}px)`, // Maintain initial top position and add dragging offset
              left: `calc(50% + ${fgimagePosition.x}px - ${(13.1 * fgscale) / 1.43}%)`, // Adjust left position based on image width
              cursor: dragging ? 'grabbing' : 'grab', // Change cursor during drag
              height: `${13.1 * fgscale}%`,
            }}
            zindex={99}
            ref={fgRef}
          >
            {foregroundImage && <img src={foregroundImage} alt="" style={{
              maxWidth: '100%', // Constrain the width of the image to the container
              maxHeight: '100%', // Constrain the height of the image to the container
              objectFit: 'contain', // Ensure the image scales properly within the div
            }}
              crossOrigin="anonymous"
              id='collectionIcon'
            />}
          </div>
        </div>
        <div id='card-name-wrapper'>
          <div className="card-field" id="card-name" contentEditable={true} suppressContentEditableWarning={true} onInput={handleNameChange} value={name} spellCheck="false">
            Card Name
          </div>
        </div>
        <div className="card-field" id="energy-cost" contentEditable={true} suppressContentEditableWarning={true} onInput={handleEnergyChange} value={energy} spellCheck="false">?</div>
        <div className="card-field" id="power" contentEditable={true} suppressContentEditableWarning={true} onInput={handlePowerChange} value={power} spellCheck="false">?</div>
        <div className="card-field" id="card-code" contentEditable={true} suppressContentEditableWarning={true} spellCheck="false">CODE</div>
        <div className="card-field" id="ability-name" contentEditable={true} suppressContentEditableWarning={true} spellCheck="false">Ability Name</div>
        <div id='ability-desc-holder'>
          <div
            className="card-field"
            id="ability-description"
            contentEditable={false}
            suppressContentEditableWarning={true}
            onInput={handleInputChange} // Update the state when the content changes
            value={content}
            spellCheck="false"
          >
          </div>
        </div>
      </div>

      {/* Modal for selecting foreground image */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        style={customModalStyles}
        contentLabel="Foreground Upload"
        appElement={document.getElementById('root') || undefined}
      >
        <h2 style={{ marginBottom: '20px', fontWeight: 'bold', fontSize: '24px' }}>Select or Upload Foreground Image</h2>

        <div style={{ marginBottom: '20px', width: '100%' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Select from existing images:
          </label>

          {/* Search input */}
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search for an icon"
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: 'var(--radius-sm, 8px)',
              border: '1px solid var(--color-border-strong, #ccc)',
              background: 'var(--color-surface-inset, #1b130c)',
              color: 'var(--color-text-primary, #f6ecdd)',
              fontSize: '16px',
            }}
          />

          {/* Display matched options below */}
          {searchTerm && filteredIcons.length > 0 && (
            <ul
              style={{
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: '200px',
                overflowY: 'auto',
                margin: '8px 0 0',
                padding: '0',
                backgroundColor: 'var(--color-surface-2, #2f2317)',
                borderRadius: 'var(--radius-sm, 8px)',
                border: '1px solid var(--color-border, rgba(255,255,255,0.1))',
                boxShadow: 'var(--shadow-low, 0px 4px 6px rgba(0, 0, 0, 0.1))',
                zIndex: 1,
              }}
            >
              {filteredIcons.map((icon, index) => (
                <li
                  key={index}
                  onClick={() => handleOptionClick(icon.url, icon.name)}
                  style={{
                    padding: '10px',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--color-border, rgba(255,255,255,0.08))',
                    listStyle: 'none',
                    color: 'var(--color-text-primary, #f6ecdd)',
                  }}
                >
                  {icon.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* "OR" text */}
        <div style={{ margin: '20px 0', textAlign: 'center', fontSize: '20px', fontWeight: 'bold' }}>OR</div>

        {/* File Upload */}
        <div style={{ marginBottom: '20px' }}>
          <input
            type="file"
            accept="image/*"
            ref={foregroundRef} // Hidden input for file upload
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => foregroundRef.current.click()}
            style={{
              background: 'var(--gradient-brand, linear-gradient(135deg,#b56d22,#d8a23e))',
              color: 'white',
              padding: '11px 22px',
              border: 'none',
              borderRadius: 'var(--radius-pill, 999px)',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              boxShadow: 'var(--shadow-low, 0 5px 10px rgba(0,0,0,0.2))',
            }}
          >
            Upload Your Own Image
          </button>
        </div>

        {/* Confirm and close modal */}
        <button
          onClick={handleModalConfirm}
          disabled={!selectedURL} // Disable if no option is selected
          style={{
            backgroundColor: selectedURL ? 'var(--color-teal, #1f8f7a)' : 'var(--color-surface-2, #ccc)',
            color: selectedURL ? 'white' : 'var(--color-text-muted, #888)',
            padding: '11px 22px',
            border: 'none',
            borderRadius: 'var(--radius-pill, 999px)',
            cursor: selectedURL ? 'pointer' : 'not-allowed',
            fontSize: '16px',
            fontWeight: 600,
            boxShadow: selectedURL ? 'var(--shadow-low, 0 5px 10px rgba(0,0,0,0.2))' : 'none',
          }}
        >
          Confirm
        </button>
      </Modal>
    </div>
  );
};

export default CardEditor;
